import { Component, inject, NgZone, OnDestroy, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { ConnectionService } from "@services/connection-service";
import { EventLoggerService } from "@services/event-logger-service";
import { Subscription } from "rxjs";
import { AppConfig } from "../../app.config";
import { MatBottomSheet } from "@angular/material/bottom-sheet";
import { CityClimateInfoComponent } from "@components/city-climate-info/city-climate-info.component";
import { CurrentComponentService } from "@services/current-component";
import { OnboardingLocationService } from "@services/onboarding/onboarding-location.service";
import { UserService } from "@services/user";

@Component({
  selector: "onboarding-location",
  templateUrl: "./onboarding-location.component.html",
  standalone: false,
})
export class OnboardingLocationComponent implements OnDestroy, OnInit {
  private readonly conn: ConnectionService = inject(ConnectionService);
  private readonly router: Router = inject(Router);
  private readonly eventLogger: EventLoggerService = inject(EventLoggerService);
  private readonly zone: NgZone = inject(NgZone);
  protected readonly appConfig: AppConfig = inject(AppConfig);
  private readonly bottomSheet: MatBottomSheet = inject(MatBottomSheet);
  private readonly currentComponent: CurrentComponentService = inject(CurrentComponentService);
  private readonly userService: UserService = inject(UserService);
  private readonly locationService: OnboardingLocationService = inject(OnboardingLocationService);

  subscriptions: Subscription[] = [];
  user: any;

  get isDravidianScriptUser(): boolean {
    return this.userService.dravidianScripts.includes(this.user?.get('languagePreference'));
  }

  get showHairFirst(): boolean {
    return this.user?.get('Gender') === this.appConfig.Shared.Gender.MALE;
  }
  showInfo: boolean = false;
  loading: boolean = false;

  ngOnInit(): void {
    this.user = this.conn.getActingUser();

    this.currentComponent.set(this);

    this.eventLogger.cleverTapEvent(
      "pageOpen",
      JSON.stringify({ pageName: "onboarding-location" })
    );
  }

  openCitySheet(): void {
    const bottomsheetRef = this.bottomSheet.open(CityClimateInfoComponent);
    bottomsheetRef.afterDismissed().subscribe((response: boolean) => {
      if (response === true) this.getLocation();
      else if (response === false) this.continue();
    });
  }

  async continue(): Promise<void> {
    await this.locationService.skipLocation();
    if (this.showInfo) {
      this.eventLogger.cleverTapEvent(
        "click",
        JSON.stringify({ name: "Onboarding-Location-Details-Popup" })
      );
    }

    await this.navigateAfterLocation();
  }

  async getLocation(): Promise<any> {
    if (this.showInfo) {
      this.eventLogger.cleverTapEvent(
        "click",
        JSON.stringify({ name: "Onboarding-Location-Details-Popup" })
      );
    }
    this.loading = true;
    const granted = await this.locationService.captureLocation();
    this.loading = false;
    if (!granted) return;
    await this.navigateAfterLocation();
  }

  private async navigateAfterLocation(): Promise<void> {
    if (this.user?.get('PrivateMainConcern')) {
      await this.router.navigate(['/user'], { queryParams: { tab: 'home' } });
      return;
    }

    await this.zone.run(async (): Promise<boolean> => this.router.navigate(['/onboarding/concern'], {
      queryParams: { redirectOnBack: '/home' },
    }));
  }

  back(): void {
    this.router.navigate(['/onboarding/gender'], { queryParams: { fromLocationPage: true } });
  }

  /** Android back: behave the same as the header back button. */
  async handleBackPress(): Promise<boolean> {
    this.back();
    return true;
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub: any): void => sub.unsubscribe());
    this.currentComponent.remove(this);
  }
}
